import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useAppAuth } from '@/contexts/app-auth.context'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Webhook } from 'lucide-react'
import {
  adminPublicApiClient,
  companyPublicApiClient,
  type PlatformWebhookSubscriptionRow,
} from '@/lib/api/admin-public-api.client'
import {
  DetailField,
  PLATFORM_PAGE_SIZE,
  SectionTabs,
  formatTimestamp,
} from './platform-shared'
import { ROUTE_REGISTRY } from '@/lib/routes'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { FloatingPagination } from '@/components/ui/FloatingPagination'

export function PlatformSubscriptionsPage() {
  const { isInitialized, currentCompany, isSuperAdmin } = useAppAuth()
  const companyId = currentCompany?.id
  const [subscriptions, setSubscriptions] = useState<
    PlatformWebhookSubscriptionRow[] | null
  >(null)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState<PlatformWebhookSubscriptionRow | null>(
    null,
  )
  // Guards against a slow response for a previous page landing after a
  // newer one and overwriting it
  const requestSeq = useRef(0)

  const loadPage = useCallback(
    async (targetPage: number) => {
      if (!companyId) return
      const requestId = ++requestSeq.current
      try {
        setLoading(true)
        const params = { limit: PLATFORM_PAGE_SIZE, page: String(targetPage) }
        const result = isSuperAdmin
          ? await adminPublicApiClient.listWebhookSubscriptions({
              ...params,
              companyId,
            })
          : await companyPublicApiClient.listWebhookSubscriptions(params)
        if (requestId !== requestSeq.current) return
        setSubscriptions(result.items)
        setTotal(result.total)
        setPage(result.page)
      } catch (error) {
        if (requestId !== requestSeq.current) return
        toast.error(
          error instanceof Error
            ? error.message
            : 'Failed to load subscriptions',
        )
        setSubscriptions([])
      } finally {
        if (requestId === requestSeq.current) setLoading(false)
      }
    },
    [companyId, isSuperAdmin],
  )

  useEffect(() => {
    if (!isInitialized) return
    loadPage(1)
  }, [isInitialized, loadPage])

  if (!isInitialized) return null

  return (
    <div className="h-full overflow-y-auto mt-16">
      <FloatingActionIslandProvider>
        <div className="space-y-6 p-6 pt-0">
          <div className="mt-6">
            <PageHeader title="Webhooks" />
          </div>
          <SectionTabs
            tabs={[
              {
                label: 'Usage',
                to: ROUTE_REGISTRY.WEBHOOKS.path,
                active: false,
              },
              {
                label: 'Endpoints',
                to: ROUTE_REGISTRY.WEBHOOKS_ENDPOINTS.path,
                active: true,
              },
              {
                label: 'Deliveries',
                to: ROUTE_REGISTRY.WEBHOOKS_DELIVERIES.path,
                active: false,
              },
            ]}
          />

          <Card className="w-full">
            <CardContent className="p-0">
              {subscriptions === null ? (
                <div className="divide-y">
                  {[...Array(4)].map((_, index) => (
                    <div key={index} className="flex space-x-4 px-6 py-4">
                      <Skeleton className="h-4 w-64" />
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  ))}
                </div>
              ) : subscriptions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <Webhook className="h-8 w-8 text-gray-400 mb-4" />
                  <h3 className="mb-2 text-lg font-semibold">
                    No subscriptions yet
                  </h3>
                  <p className="max-w-sm text-sm text-muted-foreground">
                    Subscribe an endpoint via POST /v1/webhooks — every
                    subscription shows up here.
                  </p>
                </div>
              ) : (
                <div
                  className={cn(
                    'transition-opacity duration-200',
                    loading && 'pointer-events-none opacity-50',
                  )}
                >
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Endpoint</TableHead>
                        <TableHead>Events</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subscriptions.map(subscription => (
                        <TableRow
                          key={subscription.id}
                          className="cursor-pointer"
                          onClick={() => setDetail(subscription)}
                        >
                          <TableCell className="max-w-[320px] truncate font-mono text-sm">
                            {subscription.url}
                          </TableCell>
                          <TableCell className="font-mono text-sm text-muted-foreground">
                            {subscription.events.join(', ')}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {formatTimestamp(subscription.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <FloatingPagination
                    componentId="endpoints-pagination"
                    itemType="endpoints"
                    total={total}
                    page={page}
                    pageSize={Number(PLATFORM_PAGE_SIZE)}
                    loading={loading}
                    onPage={loadPage}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Sheet
          open={detail !== null}
          onOpenChange={open => !open && setDetail(null)}
        >
          <SheetContent className="w-full overflow-y-auto p-6 sm:max-w-[520px]">
            {detail && (
              <>
                <SheetHeader>
                  <SheetTitle className="font-mono text-sm">
                    {detail.id}
                  </SheetTitle>
                  <SheetDescription>
                    {formatTimestamp(detail.createdAt)}
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-4 divide-y">
                  <DetailField label="Endpoint">{detail.url}</DetailField>
                  <DetailField label="Events">
                    {detail.events.join(', ')}
                  </DetailField>
                  <DetailField label="Created">
                    {formatTimestamp(detail.createdAt)}
                  </DetailField>
                </div>

                <p className="mt-3 text-xs text-muted-foreground">
                  The signing secret is shown only once at creation and is never
                  displayed here.
                </p>

                <Link
                  to={`${ROUTE_REGISTRY.WEBHOOKS_DELIVERIES.path}?subscriptionId=${detail.id}`}
                  className="mt-4 inline-block text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                >
                  View deliveries for this endpoint →
                </Link>
              </>
            )}
          </SheetContent>
        </Sheet>
        <FloatingActionIsland />
      </FloatingActionIslandProvider>
    </div>
  )
}
